import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { MagnifyingGlass, Headset, X } from "@phosphor-icons/react";

/**
 * Barra di ricerca clienti nella dashboard.
 * Digitando cerca clienti (name/surname/phone/email/piva); cliccando un risultato apre `/assistenze/{id}`.
 * Nascosta su mobile (hidden lg:block).
 */
export default function DashboardCustomerSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Debounced fetch
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/customers", { params: { q: s } });
        setResults((data || []).slice(0, 8));
        setHighlighted(0);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  // Click outside → close
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (c) => {
    setOpen(false);
    setQ("");
    setResults([]);
    navigate(`/assistenze/${c.id}`);
  };

  const onKeyDown = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlighted((i) => Math.min(results.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") { e.preventDefault(); pick(results[highlighted]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div className="hidden lg:block" data-testid="dashboard-customer-search-wrapper">
      <div ref={containerRef} className="relative w-72">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            placeholder="Cerca cliente…"
            className="pl-9 pr-8 h-9 rounded-full bg-card border-border/70 text-sm focus-visible:ring-1 focus-visible:ring-primary/60"
            data-testid="dashboard-customer-search-input"
          />
          {q && (
            <button
              type="button"
              onClick={() => { setQ(""); setResults([]); inputRef.current?.focus(); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 grid place-items-center rounded-full hover:bg-secondary text-muted-foreground"
              aria-label="Cancella ricerca"
              data-testid="dashboard-customer-search-clear"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {open && q.trim().length >= 2 && (
          <div className="absolute right-0 mt-2 w-96 rounded-lg border border-border bg-card shadow-lg z-40 overflow-hidden" data-testid="dashboard-customer-search-dropdown">
            {loading && (
              <div className="p-4 text-center text-xs font-mono text-muted-foreground">Ricerca in corso…</div>
            )}
            {!loading && results.length === 0 && (
              <div className="p-4 text-center text-xs font-mono text-muted-foreground">— Nessun cliente trovato —</div>
            )}
            {!loading && results.map((c, i) => (
              <button
                key={c.id}
                type="button"
                onClick={() => pick(c)}
                onMouseEnter={() => setHighlighted(i)}
                className={`w-full text-left px-4 py-2.5 border-b border-border/40 last:border-b-0 flex items-center gap-3 ${i === highlighted ? "bg-primary/10" : "hover:bg-secondary/60"}`}
                style={{ transition: "background-color 0.1s ease" }}
                data-testid={`dashboard-customer-search-result-${c.id}`}
              >
                <div className="h-8 w-8 shrink-0 rounded-full bg-primary/10 text-primary grid place-items-center">
                  <Headset size={14} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{c.surname} {c.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground truncate">
                    {c.phone}
                    {c.email && <> · {c.email}</>}
                    {c.piva && <> · P.IVA {c.piva}</>}
                  </div>
                </div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-primary shrink-0">Apri →</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
